import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Archive, Copy, DatabaseBackup, Download, Lock, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGuildRights } from '@/lib/access';
import { useGuilds, useScrollToHash } from '@/lib/hooks';
import { lockingRun, upsertRun, useRuns, useShownRun, type BotRun } from '@/lib/runs';
import {
  AreaSwitches,
  DEFAULT_AREAS,
  RunProgress,
  areaLabel,
  type BackupArea,
} from '@/components/BackupParts';

/**
 * Backups (Verwaltung): create with chosen areas, restore all or some of them.
 * Runs happen in the bot; their progress comes from the run registry
 * (lib/runs.ts) and survives reloads. Restoring "Rollen & Channels" wipes the
 * server — that needs the server name typed in. One restore / clone per
 * server at a time: while one runs, everyone sees who started it.
 */

interface BackupRow {
  id: string;
  guildId: string;
  createdAt: string;
  version: 1 | 2;
  areas: BackupArea[];
  label: string | null;
  counts: { roles: number; channels: number; emojis: number; posted: number };
}

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

export function Backups() {
  const { guildId } = useParams<{ guildId: string }>();
  const qc = useQueryClient();
  const canEdit = useGuildRights(guildId).can('backups', 'edit');
  const guilds = useGuilds(true);
  const [areas, setAreas] = useState<BackupArea[]>(DEFAULT_AREAS);
  const runs = useRuns();
  const lock = lockingRun(runs.data, [guildId!]);
  const createRun = useShownRun((r) => r.action === 'create-backup' && r.guildId === guildId);
  const cloneRun = useShownRun((r) => r.action === 'guild-clone' && r.guildId === guildId);

  const { data, isLoading, error } = useQuery({
    queryKey: ['backups', guildId],
    queryFn: () =>
      api<{ backups: BackupRow[]; nightly?: { time: string; retentionDays: number } }>(
        `/api/guilds/${guildId}/backups`
      ),
    enabled: !!guildId,
  });
  useScrollToHash(!!data && !!runs.data);

  const createMut = useMutation({
    mutationFn: () =>
      api<{ run: BotRun }>('/api/actions/create-backup', { method: 'POST', body: JSON.stringify({ guildId, areas }) }),
    onSuccess: (res) => upsertRun(qc, res.run),
  });

  const deleteMut = useMutation({
    mutationFn: (backupId: string) => api(`/api/backups/${backupId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backups', guildId] }),
  });

  const backups = data?.backups ?? [];
  const sourceName = (id: string | null) => guilds.data?.guilds.find((g) => g.id === id)?.name;

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to={`/server/${guildId}`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Server-Übersicht
      </Link>
      <h1 className="mb-6 text-2xl font-semibold">Backups</h1>

      {data?.nightly && <NightlyNote {...data.nightly} />}

      {cloneRun && (
        <Card id="running" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Copy className="h-4 w-4 text-primary" />
              {cloneRun.status === 'running' ? 'Server wird geklont' : 'Server geklont'}
            </CardTitle>
            <CardDescription>
              Von {sourceName(cloneRun.sourceGuildId) ? `„${sourceName(cloneRun.sourceGuildId)}“` : 'einem anderen Server'} auf
              diesen Server.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RunProgress run={cloneRun} />
          </CardContent>
        </Card>
      )}

      {lock?.action === 'restore-backup' && (
        <div className="mb-6 flex items-start gap-2 rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>
            Gerade läuft eine Wiederherstellung (gestartet von {lock.requestedByName} um {fmtTime(lock.startedAt)}) — bis
            sie fertig ist, sind weitere Wiederherstellungen gesperrt.{' '}
            <a href={`#backup-${lock.backupId}`} className="text-primary underline-offset-2 hover:underline">
              Zum Fortschritt
            </a>
          </span>
        </div>
      )}

      {canEdit && (
        <Card id="new-backup" className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Neues Backup</CardTitle>
            <CardDescription>Wähle, was gesichert werden soll.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <AreaSwitches value={areas} onChange={setAreas} />
            <div className="flex justify-end">
              <Button
                disabled={createMut.isPending || areas.length === 0 || createRun?.status === 'running'}
                onClick={() => createMut.mutate()}
                className="gap-2"
              >
                <Archive className="h-4 w-4" />
                {createMut.isPending ? 'Starte…' : 'Backup erstellen'}
              </Button>
            </div>
            {createMut.error && <p className="text-sm text-destructive">{(createMut.error as Error).message}</p>}
            {createRun && <RunProgress run={createRun} />}
          </CardContent>
        </Card>
      )}

      {isLoading && <p className="text-muted-foreground">Lade Backups…</p>}
      {error && <p className="text-destructive">Fehler: {(error as Error).message}</p>}
      {!isLoading && backups.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Backups.</p>}

      <div className="space-y-2">
        {backups.map((b) => (
          <BackupCard
            key={b.id}
            guildId={guildId!}
            backup={b}
            canEdit={canEdit}
            lock={lock}
            deleting={deleteMut.isPending && deleteMut.variables === b.id}
            onDelete={() => {
              if (confirm(`Backup vom ${new Date(b.createdAt).toLocaleString('de-DE')} wirklich löschen?`)) {
                deleteMut.mutate(b.id);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The nightly dump of the whole bot DB (services/backup) next to the
 * per-server backups here — what it covers, how long it's kept, who can
 * restore it. Retention comes from the server's BACKUP_RETENTION_DAYS.
 */
function NightlyNote({ time, retentionDays }: { time: string; retentionDays: number }) {
  return (
    <div className="mb-6 flex items-start gap-3 rounded-md border bg-card p-3 text-sm">
      <DatabaseBackup className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="space-y-1">
        <div className="font-medium">
          Automatische Sicherung: jede Nacht um {time} Uhr, aufbewahrt {retentionDays} Tage
        </div>
        <p className="text-muted-foreground">
          Zusätzlich zu den Backups auf dieser Seite sichert der Server jede Nacht die komplette Bot-Datenbank aller
          Server: Einstellungen, Panels, XP, Warnungen, Zugriffsrechte, Aktions-Log und auch die Backups hier. Die
          Discord-Kanäle und -Rollen selbst sind darin nicht enthalten — dafür sind die Backups auf dieser Seite da.
        </p>
        <p className="text-muted-foreground">
          Zurückspielen kann diese Sicherung nur der Bot-Admin direkt auf dem Server. Die Backups auf dieser Seite
          bleiben, bis jemand sie löscht.
        </p>
      </div>
    </div>
  );
}

function BackupCard({
  guildId,
  backup: b,
  canEdit,
  lock,
  deleting,
  onDelete,
}: {
  guildId: string;
  backup: BackupRow;
  canEdit: boolean;
  /** Running restore / clone on this server (blocks restoring). */
  lock: BotRun | null;
  deleting: boolean;
  onDelete: () => void;
}) {
  const qc = useQueryClient();
  const guilds = useGuilds(true);
  const serverName = guilds.data?.guilds.find((g) => g.id === guildId)?.name ?? '';
  const run = useShownRun((r) => r.action === 'restore-backup' && r.guildId === guildId && r.backupId === b.id);
  const [open, setOpen] = useState(!!run);
  const [areas, setAreas] = useState<BackupArea[]>(b.areas);
  const [confirmName, setConfirmName] = useState('');
  const rebuild = areas.includes('structure');

  // A restore of this backup started elsewhere (or before a reload): show it.
  const runId = run?.id;
  useEffect(() => {
    if (runId) setOpen(true);
  }, [runId]);

  const restoreMut = useMutation({
    mutationFn: () =>
      api<{ run: BotRun }>('/api/actions/restore-backup', {
        method: 'POST',
        body: JSON.stringify({ guildId, backupId: b.id, areas }),
      }),
    onSuccess: (res) => {
      upsertRun(qc, res.run);
      setConfirmName('');
    },
  });

  const counts = [
    b.counts.roles ? `${b.counts.roles} Rollen` : null,
    b.counts.channels ? `${b.counts.channels} Kanäle` : null,
    b.counts.emojis ? `${b.counts.emojis} Emojis` : null,
    b.counts.posted ? `${b.counts.posted} Panels/Nachrichten` : null,
  ].filter(Boolean);

  return (
    <Card id={`backup-${b.id}`}>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-center gap-3">
          <Archive className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1 basis-60">
            <div className="text-sm font-medium">
              {new Date(b.createdAt).toLocaleString('de-DE')}
              {b.label && <span className="ml-2 font-normal text-muted-foreground">· {b.label}</span>}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {b.areas.map((a) => (
                <span key={a} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                  {areaLabel(a)}
                </span>
              ))}
              {b.version === 1 && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">altes Format</span>
              )}
            </div>
            {counts.length > 0 && <div className="mt-1 text-xs text-muted-foreground">{counts.join(' · ')}</div>}
          </div>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
              <Download className="mr-1 h-4 w-4" /> Laden…
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="icon" disabled={deleting} title="Löschen" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        {run && !open && <RunProgress run={run} />}

        {open && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="text-sm font-medium">Was soll wiederhergestellt werden?</div>
            <AreaSwitches value={areas} onChange={setAreas} available={b.areas} disabled={!!lock} />
            {rebuild ? (
              <div className="space-y-2 rounded-md border border-destructive/60 p-2 text-sm">
                <p className="text-destructive">
                  Mit „Rollen &amp; Channels“ werden <b>alle Kanäle und Rollen gelöscht</b> und aus dem Backup neu
                  aufgebaut. Panels und Nachrichten postet der Bot danach neu.
                </p>
                <div>
                  <div className="mb-1 text-xs text-muted-foreground">
                    Zum Bestätigen den Servernamen eintippen: <b>{serverName}</b>
                  </div>
                  <Input
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={serverName}
                    disabled={!!lock}
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Ohne „Rollen &amp; Channels“ wird nichts gelöscht: Einstellungen werden auf die Rollen und Kanäle mit
                gleichem Namen gelegt.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-end gap-3">
              {lock && lock.id !== run?.id && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" />
                  Gesperrt: {lock.action === 'guild-clone' ? 'Klonen' : 'Wiederherstellen'} läuft (gestartet von{' '}
                  {lock.requestedByName} um {fmtTime(lock.startedAt)})
                </span>
              )}
              <Button
                variant={rebuild ? 'destructive' : 'default'}
                disabled={
                  restoreMut.isPending ||
                  !!lock ||
                  areas.length === 0 ||
                  (rebuild && confirmName.trim() !== serverName)
                }
                onClick={() => {
                  if (rebuild || confirm('Ausgewählte Bereiche aus diesem Backup laden?')) restoreMut.mutate();
                }}
              >
                {restoreMut.isPending ? 'Starte…' : 'Wiederherstellen'}
              </Button>
            </div>
            {restoreMut.error && <p className="text-sm text-destructive">{(restoreMut.error as Error).message}</p>}
            {run && <RunProgress run={run} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
